import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Stethoscope,
  Building2,
  UserCheck,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Wallet,
  TrendingUp,
  Loader2
} from 'lucide-react';

const RealtimeKPIs = () => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKPIs();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadKPIs, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadKPIs = async () => {
    try {
      const response = await adminAPI.getRealtimeKPIs();
      setKpis(response.data);
    } catch (error) {
      console.error('Error loading KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
          <Card key={i} className="border-0 shadow-sm animate-pulse">
            <CardContent className="p-4">
              <div className="h-16 bg-slate-100 rounded-lg" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!kpis) return null;

  const kpiCards = [
    {
      label: 'Utilisateurs totaux',
      value: kpis.user_counts?.total || 0,
      icon: Users,
      color: 'blue',
      subtext: `+${kpis.new_users_this_week || 0} cette semaine`,
      trend: 'up'
    },
    {
      label: 'Patients',
      value: kpis.user_counts?.patients || 0,
      icon: UserCheck,
      color: 'sky',
    },
    {
      label: 'Professionnels',
      value: kpis.user_counts?.professionals || 0,
      icon: Stethoscope,
      color: 'red',
    },
    {
      label: 'Établissements',
      value: kpis.user_counts?.facilities || 0,
      icon: Building2,
      color: 'indigo',
    },
    {
      label: "RDV aujourd'hui",
      value: kpis.appointments_today?.total || 0,
      icon: Calendar,
      color: 'purple',
      subtext: `${kpis.appointments_today?.confirmed || 0} confirmés`,
    },
    {
      label: 'RDV en attente',
      value: kpis.appointments_today?.pending || 0,
      icon: Clock,
      color: 'amber',
    },
    {
      label: 'RDV terminés',
      value: kpis.appointments_today?.completed || 0,
      icon: CheckCircle2,
      color: 'emerald',
    },
    {
      label: 'Actions en attente',
      value: kpis.pending_actions?.total || 0,
      icon: AlertCircle,
      color: kpis.pending_actions?.total > 0 ? 'orange' : 'slate',
      subtext: kpis.pending_actions?.total > 0 
        ? `${kpis.pending_actions?.kyc || 0} KYC, ${kpis.pending_actions?.professional_validations || 0} pros`
        : 'Tout est à jour',
      highlight: kpis.pending_actions?.total > 0
    },
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'text-blue-500' },
    sky: { bg: 'bg-sky-100', text: 'text-sky-600', icon: 'text-sky-500' },
    red: { bg: 'bg-red-100', text: 'text-red-600', icon: 'text-red-500' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', icon: 'text-indigo-500' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'text-purple-500' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', icon: 'text-amber-500' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: 'text-emerald-500' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', icon: 'text-orange-500' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'text-slate-500' },
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="realtime-kpis">
      {kpiCards.map((kpi, index) => {
        const colors = colorClasses[kpi.color] || colorClasses.blue;
        const Icon = kpi.icon;
        
        return (
          <Card 
            key={index} 
            className={`border-0 shadow-sm hover:shadow-md transition-shadow ${kpi.highlight ? 'ring-2 ring-orange-200' : ''}`}
            data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    {kpi.label}
                  </p>
                  <p className={`text-2xl md:text-3xl font-bold ${colors.text}`}>
                    {kpi.value.toLocaleString()}
                  </p>
                  {kpi.subtext && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      {kpi.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                      {kpi.subtext}
                    </p>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${colors.icon}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default RealtimeKPIs;
